// QueueChip.jsx — what is waiting, in the header.
//
// ── WHAT THIS REPLACES ──
// Two segmented pills, "Home Delivery" and "On-Store Pickup", side by side in
// the header on every screen with a red blinking count on each. That shape is
// the single thing the old portal and this one still had in common at a
// glance, and it was doing two jobs at once: telling you how much work was
// waiting, and selecting which half of the Orders screen you were looking at.
//
// ── WHY ONE CHIP ──
// The number is worth seeing from anywhere. The *selector* is only meaningful
// on one screen. So the chip shows the total, and the choice moves inside it:
// one object in the header, opened when you want the split, and a jump
// straight to the orders it refers to.
//
// ── WHY THE BLINK IS GONE ──
// The old badge ran `blink-continuous 0.8s infinite` whenever anything was
// pending, which on a normal trading day is all day. An alert that is always
// firing is not an alert; it is a thing people learn to look past. The chip
// goes amber when work is waiting and stays still.

import React, { useEffect, useRef, useState } from "react";

export default function QueueChip({ mode, onPick, delivery = 0, pickup = 0 }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  const total = delivery + pickup;
  const waiting = total > 0;

  /* Click-away and Escape. A dropdown that can only be closed by the control
     that opened it is a dropdown that gets left open. */
  useEffect(() => {
    if (!open) return;
    const away = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false);
    };
    const key = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  const choose = (which) => {
    setOpen(false);
    onPick?.(which);
  };

  return (
    <div className="mly-queue" ref={box}>
      <button
        type="button"
        className={"mly-queue__chip" + (waiting ? " is-waiting" : "")}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <span className="mly-queue__dot" />
        <strong>{total}</strong>
        <span className="mly-queue__word">
          {total === 1 ? "order waiting" : "orders waiting"}
        </span>
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={"mly-queue__caret" + (open ? " is-open" : "")}
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open ? (
        <div className="mly-queue__panel" role="menu">
          <Line
            label="Home delivery"
            count={delivery}
            active={mode === "delivery"}
            onClick={() => choose("delivery")}
            icon={
              <>
                <rect x="1" y="4" width="14" height="11" rx="1.6" />
                <path d="M15 8.5h4l3 4.5V15h-7Z" />
                <circle cx="5.5" cy="18" r="2.3" />
                <circle cx="18" cy="18" r="2.3" />
              </>
            }
          />
          <Line
            label="On-store pickup"
            count={pickup}
            active={mode === "pickup"}
            onClick={() => choose("pickup")}
            icon={
              <>
                <path d="M3.5 9 12 2.5 20.5 9v10.5a1.6 1.6 0 0 1-1.6 1.6H5.1a1.6 1.6 0 0 1-1.6-1.6Z" />
                <path d="M9.4 21.1v-7h5.2v7" />
              </>
            }
          />
          {total === 0 ? (
            <p className="mly-queue__clear">Nothing waiting. All caught up.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Line({ label, count, active, onClick, icon }) {
  return (
    <button
      type="button"
      className={"mly-queue__line" + (active ? " is-active" : "")}
      onClick={onClick}
      role="menuitem"
    >
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {icon}
      </svg>
      <span className="mly-queue__label">{label}</span>
      <span className={"mly-queue__count" + (count > 0 ? " is-on" : "")}>
        {count}
      </span>
    </button>
  );
}
